import {
  ApplicationLink,
  BlogPost,
  ResearchPaper,
  Project,
  Experience,
  DiscussionSession,
} from './types';

export const APPLICATION_LINKS: ApplicationLink[] = [
  {
    id: 'taxcanvaspro',
    name: 'TaxCanvasPro',
    description:
      'AI-native Australian tax structuring app for modelling entities, distributions, ledgers, scenarios, and exportable workpapers.',
    href: 'https://taxcanvaspro.com',
    category: 'Flagship Tax Tool',
    audience: 'Tax advisers, founders, accountants',
    ctaLabel: 'Open TaxCanvasPro',
    status: 'live',
    accent: 'violet',
  },
  {
    id: 'aussie-ledger',
    name: 'AussieLedger',
    description:
      'Open-source accounting journal and bookkeeping software for local Australian tax return preparation workflows.',
    href: 'https://aussieledger.techtaitan.com',
    category: 'Open Source Accounting',
    audience: 'Bookkeepers, accountants, builders',
    ctaLabel: 'Explore AussieLedger',
    status: 'live',
    accent: 'emerald',
    sourceHref: 'https://github.com/tech-taitan/aussie-ledger',
  },
  {
    id: 'finance-console',
    name: 'Finance Console',
    description:
      'Scenario modelling, portfolio review, and finance analysis tools for professional decision support.',
    href: 'https://finance.techtaitan.dev',
    category: 'Finance',
    audience: 'Finance teams and advisers',
    ctaLabel: 'Launch Console',
    status: 'beta',
    accent: 'blue',
  },
  {
    id: 'client-portal',
    name: 'Client Portal',
    description:
      'Secure workspace for client documents, advisory workflows, reports, and project updates.',
    href: 'https://portal.techtaitan.dev',
    category: 'Operations',
    audience: 'Clients and collaborators',
    ctaLabel: 'Enter Portal',
    status: 'live',
    accent: 'amber',
  },
  {
    id: 'knowledge-base',
    name: 'Knowledge Base',
    description:
      'Technical references, implementation notes, tax logic notes, and reusable AI engineering patterns.',
    href: 'https://docs.techtaitan.dev',
    category: 'Documentation',
    audience: 'Developers and domain experts',
    ctaLabel: 'Read Notes',
    status: 'live',
    accent: 'slate',
  },
  {
    id: 'experiment-studio',
    name: 'Experiment Studio',
    description:
      'Sandbox for AI prototypes, model tests, professional workflow demos, and early product ideas.',
    href: 'https://studio.techtaitan.dev',
    category: 'Labs',
    audience: 'Product experiments',
    ctaLabel: 'View Experiments',
    status: 'beta',
    accent: 'rose',
  },
];

export const BLOG_POSTS: BlogPost[] = [
  {
    id: '1',
    slug: 'ai-risk-management-finance',
    title: 'AI Risk Management in Financial Services',
    description:
      'An in-depth look at regulatory compliance and risk mitigation for AI-driven financial models. We explore the intersection of Basel IV and modern LLM governance.',
    date: 'Jan 15, 2026',
    category: 'Finance',
    tags: ['AI', 'Compliance', 'Risk', 'Fintech'],
    readingTime: '8 min read',
    author: 'TechTaitan',
    content: `
# AI Risk Management in Financial Services
Risk management in financial services is undergoing a paradigm shift. With the advent of Large Language Models (LLMs) and advanced quantitative algorithms.
`,
    image:
      'https://images.unsplash.com/photo-1611974714851-482061323675?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: '2',
    slug: 'quantum-computing-cryptography',
    title: 'Post-Quantum Cryptography in Modern Banking',
    description:
      'Preparing financial infrastructure for the quantum era. This post analyzes Lattice-based encryption vs. current RSA standards.',
    date: 'Jan 10, 2026',
    category: 'Security',
    tags: ['Quantum', 'Security', 'Banking'],
    readingTime: '12 min read',
    author: 'TechTaitan',
    content: `Quantum computing poses a significant threat to modern encryption...`,
    image:
      'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: '3',
    slug: 'agentic-workflows-swarm',
    title: 'Swarm Intelligence: Beyond Single Agent RAG',
    description:
      'Why single-agent architectures are failing in complex enterprise environments and how swarm-based consensus models are solving state synchronization issues. We delve into recursive task decomposition and agent-to-agent negotiation protocols.',
    date: 'Jan 05, 2026',
    category: 'Architecture',
    tags: ['Agents', 'Swarm', 'RAG'],
    readingTime: '15 min read',
    author: 'TechTaitan',
    content: `Moving beyond the basic RAG pipeline...`,
    image:
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: '4',
    slug: 'gpu-optimization-strategies',
    title: 'Inferencing at Scale: A 2026 Strategy',
    description:
      'Deep dive into KV-cache optimization and multi-tenant GPU utilization for fintech workloads.',
    date: 'Dec 28, 2025',
    category: 'DevOps',
    tags: ['GPU', 'Scale', 'Inference'],
    readingTime: '6 min read',
    author: 'TechTaitan',
    content: `Optimization is no longer optional...`,
    image:
      'https://images.unsplash.com/photo-1591439657443-a28a39fb20dd?auto=format&fit=crop&q=80&w=800',
  },
  {
    id: '5',
    slug: 'ethics-of-autonomous-trading',
    title: 'The Ethics of Autonomous Liquidity Provision',
    description:
      'Is high-frequency market making by AI ethical? Exploring the moral hazards of black-box liquidity and the potential for unintended market manipulation through algorithmic feedback loops.',
    date: 'Dec 20, 2025',
    category: 'Ethics',
    tags: ['Trading', 'Ethics', 'Markets'],
    readingTime: '10 min read',
    author: 'TechTaitan',
    content: `The question of morality in market making...`,
    image:
      'https://images.unsplash.com/photo-1642104704074-907c0698cbd9?auto=format&fit=crop&q=80&w=800',
  },
];

export const RESEARCH_PAPERS: ResearchPaper[] = [
  {
    id: 'r1',
    slug: 'attention-is-all-you-need',
    title: 'Attention Is All You Need',
    authors: ['Vaswani et al.'],
    publication: 'NeurIPS 2017',
    date: '2017',
    rating: 5,
    takeaways: [
      'Introduced the Transformer architecture',
      'Replaced recurrence and convolution with self-attention',
      'Foundation for modern LLMs like GPT and Claude',
    ],
    category: 'Architecture',
    tags: ['Transformer', 'Deep Learning', 'NLP'],
    url: 'https://arxiv.org/abs/1706.03762',
    summary:
      'This seminal paper introduced the Transformer, a model architecture relying entirely on attention mechanisms to draw global dependencies between input and output.',
  },
];

export const PROJECTS: Project[] = [
  {
    id: 'p1',
    title: 'Agentic Finance Suite',
    description:
      'An autonomous suite for real-time portfolio rebalancing and risk analysis using Gemini-based agents.',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnail:
      'https://images.unsplash.com/photo-1551288049-bbda38a5f85d?auto=format&fit=crop&q=80&w=800',
    techStack: ['React', 'TypeScript', 'Gemini API', 'Firebase'],
    githubUrl: 'https://github.com',
    demoUrl: 'https://demo.com',
    likes: 124,
    repository: [
      {
        name: 'src',
        type: 'folder',
        children: [
          {
            name: 'agents',
            type: 'folder',
            children: [
              {
                name: 'RiskAgent.ts',
                type: 'code',
                language: 'typescript',
                content: `export class RiskAgent {\n  constructor(private context: MarketContext) {}\n\n  async analyze(portfolio: Portfolio) {\n    const exposure = await this.calculateExposure(portfolio);\n    if (exposure > MAX_THRESHOLD) {\n      return this.triggerHedge(exposure);\n    }\n    return { status: 'stable' };\n  }\n}`,
              },
              {
                name: 'PortfolioManager.ts',
                type: 'code',
                language: 'typescript',
                content: `export class PortfolioManager {\n  private agents: Agent[] = [];\n\n  async rebalance() {\n    // Multi-agent consensus logic\n    const results = await Promise.all(this.agents.map(a => a.vote()));\n    return this.executeConsensus(results);\n  }\n}`,
              },
            ],
          },
          {
            name: 'App.tsx',
            type: 'code',
            language: 'typescript',
            content: `import React from 'react';\nimport { Dashboard } from './components';\n\nconst App = () => {\n  return <Dashboard theme="enterprise" />;\n};`,
          },
        ],
      },
      {
        name: 'docs',
        type: 'folder',
        children: [
          {
            name: 'architecture.md',
            type: 'markdown',
            content: `# Architecture Overview\n\nThe Agentic Finance Suite utilizes a decentralized swarm of specialized agents to monitor global market liquidity.\n\n## Data Pipeline\n1. Socket Feed (Real-time)\n2. VectorDB Enrichment\n3. Gemini Reasoning Core\n4. Execution Engine`,
          },
        ],
      },
      {
        name: 'assets',
        type: 'folder',
        children: [
          {
            name: 'workflow_schema.svg',
            type: 'image',
            content:
              'https://images.unsplash.com/photo-1551288049-bbda38a5f85d?auto=format&fit=crop&q=80&w=1200',
          },
        ],
      },
    ],
  },
];

export const EXPERIENCES: Experience[] = [
  {
    id: 'e1',
    company: 'FinTech Alchemist',
    role: 'Lead AI Engineer',
    period: '2024 - Present',
    description: [
      'Architected a RAG-based financial advisor agent serving 50k+ users.',
      'Reduced model inference costs by 40% through custom prompt optimization.',
      'Led a team of 5 engineers in deploying multi-modal trading assistants.',
    ],
    current: true,
  },
];

export const DISCUSSION_SESSIONS: DiscussionSession[] = [
  {
    id: 's1',
    title: 'Architecting Multi-Agent Systems for Trading',
    description:
      'A deep dive into how swarm intelligence can outperform monolithic models in high-frequency environments.',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnail:
      'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1200',
    date: 'Jan 18, 2026',
    duration: '45:20',
    category: 'Agent Coding',
    tags: ['Swarm Intelligence', 'Agents', 'Quant Finance'],
    guest: {
      name: 'Dr. Sarah Chen',
      role: 'Head of AI',
      company: 'Nexus Capital',
      avatar: 'https://i.pravatar.cc/150?u=sarah',
    },
    chapters: [
      { time: '00:00', title: 'Introduction to Swarms' },
      { time: '12:45', title: 'Managing Agent Conflicts' },
      { time: '30:10', title: 'Practical Python Implementation' },
    ],
  },
  {
    id: 's2',
    title: 'The Ethics of Autonomous Financial Agents',
    description:
      'Discussing the regulatory hurdles and moral implications of letting AI manage capital without human intervention.',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnail:
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=1200',
    date: 'Jan 12, 2026',
    duration: '38:15',
    category: 'Practical AI',
    tags: ['Ethics', 'Compliance', 'Governance'],
    guest: {
      name: 'Marcus Thorne',
      role: 'RegTech Strategist',
      company: 'Global Compliance Org',
      avatar: 'https://i.pravatar.cc/150?u=marcus',
    },
    chapters: [
      { time: '00:00', title: 'State of Regulation 2026' },
      { time: '15:20', title: 'The "Flash Crash" Risk' },
      { time: '28:40', title: 'Solutions for Auditability' },
    ],
  },
];
